import React from 'react';
import {
	Space,
	Switch,
	Tooltip,
} from 'antd';
import { Moment } from 'moment';
import { formatPercentage } from '@shared/utils/formatPercentage';
import { Values } from '@shared/utils/objectEnums';
import {
	CalculationMethodForExpensesSubjectToHireDays,
	Currencies,
	DATE_AND_TIME,
	HireUnit,
	PaymentTermLabels,
	PaymentTerms,
	TcTermsParentTypes,
} from '@shared/utils/constants';
import { toMoment } from '@shared/utils/date';
import { capitalize } from '@shared/utils/string';
import type { GetCounterpartiesResponse } from '@api/features/counterparties/getCounterparties';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { GetVesselsResponse } from '@api/features/vessels/getVessels';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type {
	Port,
	Range,
} from '@api/utils/ports';
import type { BankAccountProps } from '@api/models/bank-account';
import type { FixingCompanyProps } from '@api/models/fixing-company';
import TcTerms, { TcTermsProps } from '@api/models/tc-terms';
import CVEFormulaLabel from '@client/components/CVEFormulaLabel';
import Select from '@client/components/Select';
import { renderUtcDate } from '@client/utils/table';
import NumericInput from '@client/components/NumericInput';
import { getPortOptions } from '@client/utils/getPortAndRangeOptions';
import { getBankAccountField } from '@client/screens/fixtures/shared/sectionItems';
import getFixingCompanyOptions from '@client/utils/getFixingCompanyOptions';

const getTcEstimateTermsItems = ({
	fixtureDetails,
	tcTerms,
	charterers,
	fixingCompanies,
	vessels,
	fixtureCurrency,
	portsAndRanges,
	bankAccounts,
	onUpdate,
	hasInvoices,
}: {
	fixtureDetails: GetFixtureDetailsResponse<TcFixtureProps>;
	tcTerms: TcTerms;
	charterers: GetCounterpartiesResponse | undefined;
	fixingCompanies: Array<FixingCompanyProps>;
	vessels: GetVesselsResponse | undefined;
	fixtureCurrency: Values<typeof Currencies>;
	portsAndRanges: (Port | Range)[] | undefined;
	bankAccounts: BankAccountProps[];
	onUpdate: (attributes: Partial<TcTermsProps>) => void;
	hasInvoices?: boolean
}) => {
	const ports = (portsAndRanges ?? []).filter((p): p is Port => p.isPort === true);
	const portOptions = getPortOptions(ports);

	const isVoyage = tcTerms.parentType === TcTermsParentTypes.Voyage;

	return [
		{
			key: 'counterpartyId',
			label: 'Charterer',
			type: 'select',
			editable: true,
			value: tcTerms.counterpartyId,
			render: () => {
				const c = charterers?.find((charterer) => (
					charterer.id === tcTerms.counterpartyId
				));

				return c?.name ?? 'N/A';
			},
			options: charterers?.map((c) => ({
				label: c.name,
				value: c.id,
			})),
			inputProps: {
				'data-testid': 'charterer',
				showSearch: true,
				placeholder: 'Select Charterer',
			},
		},
		{
			key: 'fixingCompanyId',
			label: 'Fixing Company',
			type: 'select',
			editable: !isVoyage && !fixtureDetails.fixed,
			value: tcTerms.fixingCompanyId,
			options: getFixingCompanyOptions(fixingCompanies, tcTerms.fixingCompanyId),
			inputProps: {
				'data-testid': 'fixing-company',
				showSearch: true,
				placeholder: 'Select Fixing Company',
			},
		},
		{
			key: 'vesselId',
			label: 'Vessel',
			type: 'select',
			editable: !isVoyage,
			value: tcTerms.vesselId,
			render: () => {
				const vessel = vessels?.find((v) => (
					v.id === tcTerms.vesselId
				));

				return vessel?.name ?? 'N/A';
			},
			options: vessels?.map((c) => ({
				label: c.name,
				value: c.id,
			})),
			inputProps: {
				showSearch: true,
				placeholder: 'Select Vessel',
			},
		},
		{
			...getBankAccountField({ fixture: fixtureDetails, bankAccounts, required: true }),
			// Always editable in estimates so the select (and its test id) is available
			editable: true,
		},
		{
			key: 'addressCommission',
			label: 'Address Commission',
			type: 'percentage',
			value: tcTerms.addressCommission,
			editable: true,
			render: () => formatPercentage(tcTerms.addressCommission ?? 0),
			inputProps: {
				'data-testid': 'address-commission',
			},
		},
		{
			key: 'grossBallastBonus',
			label: 'Gross ballast bonus',
			type: 'currency',
			inputProps: {
				currency: fixtureCurrency,
				'data-testid': 'gross-ballast-bonus',
			},
			value: tcTerms.grossBallastBonus,
			editable: true,
		},
		{
			label: 'Laycan from',
			value: tcTerms.laycanFrom,
			key: 'laycanFrom',
			editable: true,
			type: 'date',
			inputProps: {
				'data-testid': 'laycan-from',
				time: true,
				placeholder: 'Laycan From'
			}
		},
		{
			label: 'Laycan to',
			value: tcTerms.laycanTo,
			key: 'laycanTo',
			editable: true,
			type: 'date',
			inputProps: {
				'data-testid': 'laycan-to',
				time: true,
				placeholder: 'Laycan To'
			}
		},
		{
			key: 'calculationMethodForExpensesSubjectToHireDays',
			label: 'Calculation method for hire days',
			type: 'select',
			editable: !hasInvoices,
			value: tcTerms.calculationMethodForExpensesSubjectToHireDays,
			options: [
				{
					label: (
						<CVEFormulaLabel
							type={CalculationMethodForExpensesSubjectToHireDays.ANNUALLY}
						/>
					),
					value: CalculationMethodForExpensesSubjectToHireDays.ANNUALLY,
				},
				{
					label: (
						<CVEFormulaLabel
							type={CalculationMethodForExpensesSubjectToHireDays.MONTHLY}
						/>
					),
					value: CalculationMethodForExpensesSubjectToHireDays.MONTHLY,
				},
				{
					label: 'FIXED: same rate regardless of hire or off hire duration',
					value: CalculationMethodForExpensesSubjectToHireDays.FIXED,
				},
			],
			inputProps: {
				'data-testid': 'cve-calculation-method',
				style: { height: 55 },
				showSearch: true,
				placeholder: 'Select CVE Calculation method',
			},
		},
		...(isVoyage ? [] : [
			{
				key: 'deliveryPortsOrRanges',
				label: 'Delivery port',
				type: 'select',
				editable: true,
				value: tcTerms.deliveryPortsOrRanges?.[0]?.id ?? null,
				options: portOptions,
				render: () => tcTerms.deliveryPortsOrRangesLabel,
				renderInput: ({
					value,
					onChange,
				}: { value: number | null; onChange: (v: Port[]) => void }) => (
					<Select
						placeholder="Select port"
						data-testid="delivery-ports"
						value={value}
						showSearch
						allowClear
						onChange={(v) => {
							const port = ports.find((pr) => pr.id === v);
							onChange(port == null ? [] : [port]);
						}}
						options={portOptions}
					/>
				),
			},
			{
				key: 'redeliveryPortsOrRanges',
				label: 'Redelivery port',
				type: 'select',
				editable: true,
				value: tcTerms.redeliveryPortsOrRanges?.[0]?.id ?? null,
				options: portOptions,
				render: () => tcTerms.redeliveryPortsOrRangesLabel,
				renderInput: ({
					value,
					onChange,
				}: { value: number | null; onChange: (v: Port[]) => void }) => (
					<Select
						placeholder="Select port"
						data-testid="redelivery-ports"
						value={value}
						showSearch
						allowClear
						onChange={(v) => {
							const port = ports.find((pr) => pr.id === v);
							onChange(port == null ? [] : [port]);
						}}
						options={portOptions}
					/>
				),
			},
		]),
		{
			key: 'deliveryRange',
			label: 'Delivery range',
			editable: true,
			type: 'text',
			value: tcTerms.deliveryRange,
			inputProps: {
				'data-testid': 'delivery-range',
			},
		},
		{
			key: 'redeliveryRange',
			label: 'Redelivery range',
			editable: true,
			type: 'text',
			value: tcTerms.redeliveryRange,
			inputProps: {
				'data-testid': 'redelivery-range',
			},
		},
		{
			key: 'estDeliveryDate',
			label: 'Est. delivery date',
			editable: false,
			value: fixtureDetails.estDeliveryDate,
			render: (d: Moment | null | undefined) => (
				d == null ? 'N/A' : renderUtcDate(d, DATE_AND_TIME)
			),
		},
		{
			key: 'estRedeliveryDate',
			label: 'Est. redelivery date',
			editable: false,
			value: fixtureDetails.estRedeliveryDate,
			render: (d: Moment | null | undefined) => (
				d == null ? 'N/A' : renderUtcDate(d, DATE_AND_TIME)
			),
		},
		{
			key: 'cpForm',
			label: 'CP Form',
			editable: true,
			type: 'text',
			value: tcTerms.cpForm,
			inputProps: {
				'data-testid': 'cp-form',
			},
		},
		{
			key: 'cpDate',
			label: 'CP Date',
			editable: true,
			type: 'date',
			value: tcTerms.cpDate == null ? null : toMoment(tcTerms.cpDate),
			render: (d: Moment) => renderUtcDate(d, DATE_AND_TIME),
			inputProps: {
				'data-testid': 'cp-date',
			},
		},
		{
			key: 'paymentTermValue',
			label: 'Payment Terms',
			type: 'number',
			value: tcTerms.paymentTermValue,
			editable: true,
			renderInput: ({
				value,
				onChange,
			}: { value: string; onChange: (v: any) => void }) => (
				<Space>
					<NumericInput
						style={{ width: 50 }}
						value={Number(value)}
						onChange={onChange}
						data-testid="payment-term-days"
					/>
					<div>Days from</div>
					<Select
						style={{ width: 250 }}
						allowClear={false}
						value={tcTerms.paymentTerm}
						onChange={(val: string | null) => {
							if (val != null) {
								onUpdate({ paymentTerm: val });
							}
						}}
						options={[{
							label: PaymentTermLabels[PaymentTerms.PERIOD_START],
							value: PaymentTerms.PERIOD_START,
						}, {
							label: PaymentTermLabels[PaymentTerms.PERIOD_END],
							value: PaymentTerms.PERIOD_END,
						}, {
							label: PaymentTermLabels[PaymentTerms.INVOICE_DATE],
							value: PaymentTerms.INVOICE_DATE,
						}]}
						placeholder="Select term"
					/>
				</Space>
			),
		},
		{
			key: 'invoicePerCalenderMonth',
			label: 'Invoice per calendar month (PCM)',
			type: 'switch',
			renderInput: ({
				value,
				onChange,
			}: { value: boolean | null; onChange: (v: any) => void }) => {
				return (
					<Tooltip
						title={`${tcTerms.hireUnit === HireUnit.MONTHS ? 'Cannot edit this field when hire unit is set to Months' : ''}`}
					>
						<Switch
							checked={value || false}
							disabled={tcTerms.hireUnit === HireUnit.MONTHS}
							onChange={onChange}
							data-testid="invoice-per-calendar-month"
						/>
					</Tooltip>
				);
			},
			value: tcTerms.invoicePerCalenderMonth ?? false,
			editable: true,
		},
		{
			key: 'hireUnit',
			label: 'Hire Units',
			type: 'select',
			value: tcTerms.hireUnit === HireUnit.MONTHS ? ' Per month ' : ' Per day ',
			options: Object.values(HireUnit).map((value) => ({
				label: fixtureDetails.fixed ? (
					<Tooltip title="Cannot edit this field when estimate is fixed">
						{capitalize(value)}
					</Tooltip>
				) : capitalize(value),
				value,
			})),
			editable: !fixtureDetails.fixed,
			required: true,
			inputProps: {
				showSearch: true,
			},
		},
		{
			key: 'daysPerHirePeriod',
			label: 'Days per hire period',
			value: tcTerms.daysPerHirePeriod,
			type: 'number',
			renderInput: ({
				value,
				onChange,
			}: { value: number; onChange: (v: any) => void }) => {
				if (
					tcTerms.invoicePerCalenderMonth ||
					tcTerms.hireUnit === HireUnit.MONTHS
				) {
					return (
						<Tooltip title="Cannot edit this field when PCM is on or hire unit is set to Months">
							<NumericInput
								value={null}
								disabled
								addonAfter="Days"
								data-testid="days-per-hire-period"
							/>
						</Tooltip>
					);
				}

				return (
					<NumericInput
						value={value}
						onChange={onChange}
						addonAfter="Days"
						min={1}
						data-testid="days-per-hire-period"
					/>
				);
			},
			editable: true,
		},
		{
			key: 'useUTC',
			label: 'UTC / Local time',
			type: 'select',
			options: [],
			value: tcTerms.useUTC,
			editable: true,
			render: (useUTC: boolean) => useUTC ? 'UTC' : 'Local Time',
			renderInput: ({
				value,
				onChange,
			}: { value: number; onChange: (v: any) => void }) => {
				if (tcTerms.hireUnit === HireUnit.MONTHS) {
					return (
						<Tooltip title="Cannot edit this field when hire unit is set to Months">
							<Select
								value=" - "
								disabled
								data-testid="use-utc"
							/>
						</Tooltip>
					);
				}

				return (
					<Select
						value={value}
						onChange={onChange}
						data-testid="use-utc"
						options={[
							{ label: 'UTC', value: true },
							{ label: 'Local time', value: false },
						]}
					/>
				);
			},
		},
		{
			key: 'note',
			label: 'Note',
			type: 'textarea',
			value: tcTerms.note,
			editable: true,
			inputProps: {
				rows: 2,
				placeholder: 'Enter note',
				'data-testid': 'note',
			},
		},
	];
};

export default getTcEstimateTermsItems;
